import type { ReceiptValidationContext, ValidationError } from './types';
import { ReceiptValidationError } from './types';

export function getReceiptSubmissionErrors(context: ReceiptValidationContext): {
  errors: Array<ValidationError<ReceiptValidationError>>;
  warnings: Array<ValidationError<ReceiptValidationError>>;
} {
  const errors: Array<ValidationError<ReceiptValidationError>> = [];
  const warnings: Array<ValidationError<ReceiptValidationError>> = [];

  const {
    formValues,
    isDirty,
    cashLinkFeatureEnabled,
    mandatoryCashLinkRequired,
    receiptImagesRequired,
    pliantTransactionAmount,
  } = context;

  // Check for unsaved changes FIRST - if form is dirty, only return this error
  // to force the user to save first and avoid inconsistent validation states
  if (isDirty) {
    errors.push({
      type: ReceiptValidationError.UNSAVED_CHANGES,
      translationKey: 'receipts.validation.unsaved_changes',
      details: undefined,
    });
    // Return early - don't check other validations if there are unsaved changes
    return { errors, warnings };
  }

  // Check for cash link - error if mandatory, warning if optional
  // Skip validation if this is an earnings receipt
  if (
    !formValues.earnings &&
    cashLinkFeatureEnabled &&
    !formValues.cashTransactionGroupId
  ) {
    if (mandatoryCashLinkRequired) {
      errors.push({
        type: ReceiptValidationError.MISSING_CASH_LINK,
        translationKey: 'receipts.validation.missing_cash_link',
        details: undefined,
      });
    } else {
      warnings.push({
        type: ReceiptValidationError.MISSING_CASH_LINK,
        translationKey: 'mobile.receipt.submit-cash-link-warning',
        details: undefined,
      });
    }
  }

  // Check for receipt image - error if required, warning if optional
  const hasImages =
    formValues.receiptImageIds &&
    formValues.receiptImageIds.length > 0 &&
    formValues.receiptImageIds.some((id) => id && id.trim() !== '');

  if (!hasImages) {
    if (receiptImagesRequired) {
      errors.push({
        type: ReceiptValidationError.MISSING_IMAGE,
        translationKey: 'receipts.validation.missing_image',
        details: undefined,
      });
    } else {
      warnings.push({
        type: ReceiptValidationError.MISSING_IMAGE,
        translationKey: 'mobile.receipt.submit-receipt-warning',
        details: undefined,
      });
    }
  }

  // Check for required fields
  if (!formValues.residentId) {
    errors.push({
      type: ReceiptValidationError.MISSING_RESIDENT,
      translationKey: 'receipts.validation.missing_resident',
      details: undefined,
    });
  }

  if (!formValues.transactionType) {
    errors.push({
      type: ReceiptValidationError.MISSING_TYPE,
      translationKey: 'receipts.validation.missing_type',
      details: undefined,
    });
  }

  if (!formValues.date) {
    errors.push({
      type: ReceiptValidationError.MISSING_DATE,
      translationKey: 'receipts.validation.missing_date',
      details: undefined,
    });
  }

  if (!formValues.amount || formValues.amount <= 0) {
    errors.push({
      type: ReceiptValidationError.MISSING_AMOUNT,
      translationKey: 'receipts.validation.missing_amount',
      details: undefined,
    });
  }

  // Check for service provider if required by receipt type
  if (
    formValues.serviceProviderRequired &&
    (!formValues.serviceProvider || formValues.serviceProvider.trim() === '')
  ) {
    errors.push({
      type: ReceiptValidationError.MISSING_SERVICE_PROVIDER,
      translationKey: 'receipts.validation.missing_service_provider',
      details: undefined,
    });
  }

  // Check for pliant transaction amount mismatch
  // Note: pliantTransactionAmount === 0 means no Pliant transaction (cash-only group)
  if (
    formValues.cashTransactionGroupId &&
    pliantTransactionAmount !== undefined &&
    pliantTransactionAmount !== 0 &&
    formValues.amount &&
    Math.abs(pliantTransactionAmount) !== Math.abs(formValues.amount)
  ) {
    errors.push({
      type: ReceiptValidationError.PLIANT_AMOUNT_MISMATCH,
      translationKey: 'receipts.validation.pliant_amount_mismatch',
      details: {
        receiptAmount: formValues.amount,
        pliantAmount: pliantTransactionAmount,
      },
    });
  }

  return { errors, warnings };
}

export function canSubmitReceipt(context: ReceiptValidationContext): boolean {
  const { errors } = getReceiptSubmissionErrors(context);
  return errors.length === 0;
}

export function getValidationSummary(
  errors: Array<ValidationError<ReceiptValidationError>>
): {
  hasErrors: boolean;
  errorCount: number;
  errorTypes: ReceiptValidationError[];
} {
  return {
    hasErrors: errors.length > 0,
    errorCount: errors.length,
    errorTypes: errors.map((e) => e.type),
  };
}

export function requiresCashLink(legalEntity?: {
  mandatory_cash_link_for_receipts?: boolean;
}): boolean {
  return legalEntity?.mandatory_cash_link_for_receipts ?? false;
}
